/* ─────────────────────────────────────────────────────────────
   TimbA Favourites
   File: assets/timba-favourites.js

   What it does
   ────────────
   • Stores product favourites in localStorage (key: timba_favs)
   • Wires up heart buttons on cards (.tpc-fav) and anywhere else
     marked [data-fav-toggle] — toggles, animates, persists
   • Keeps the header heart count bubble ([data-fav-count])
     in sync (initial render + every change + cross-tab via storage event)
   • Auto-detects new cards added to the page (AJAX filtering, infinite
     scroll) and wires them too via MutationObserver
   • Exposes window.TimbaFavourites API so other pages (e.g. the
     favourites page) can read/clear/listen for changes

   API
   ───
   window.TimbaFavourites.list()         → ['handle-a', 'handle-b', …]
   window.TimbaFavourites.has(handle)    → true | false
   window.TimbaFavourites.add(handle)    → adds + persists + re-renders
   window.TimbaFavourites.remove(handle) → removes + persists + re-renders
   window.TimbaFavourites.toggle(handle) → flips + returns new state
   window.TimbaFavourites.clear()        → wipes everything
   window.TimbaFavourites.count()        → number
   window.TimbaFavourites.on(eventName, cb)
       eventName: 'change' | 'add' | 'remove'
       callback receives ({ handle?, list })

   Storage shape
   ─────────────
   localStorage.timba_favs = JSON.stringify({
     v: 1,                          // schema version
     items: ['hbs-8x100', 'snk-evo-5x60', …]    // ordered newest-first
   })
   ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (window.TimbaFavourites) return; // guard against double-load

  // ── Constants ──────────────────────────────────────────────
  var STORAGE_KEY = 'timba_favs';
  var SCHEMA_VERSION = 1;
  var MAX_FAVS = 200; // hard cap so localStorage doesn't bloat

  // ── State ──────────────────────────────────────────────────
  var items = [];                 // current list of handles
  var listeners = {               // event subscribers
    change: [],
    add:    [],
    remove: []
  };

  // ── Storage helpers ────────────────────────────────────────
  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.items)) return parsed.items.slice();
      return [];
    } catch (e) {
      console.warn('[TimbaFavourites] read failed:', e);
      return [];
    }
  }

  function write(list) {
    try {
      var trimmed = list.slice(0, MAX_FAVS);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        v: SCHEMA_VERSION,
        items: trimmed
      }));
    } catch (e) {
      console.warn('[TimbaFavourites] write failed (storage full?):', e);
    }
  }

  // ── Event helpers ──────────────────────────────────────────
  function fire(eventName, payload) {
    var arr = listeners[eventName] || [];
    for (var i = 0; i < arr.length; i++) {
      try { arr[i](payload); } catch (e) { console.warn(e); }
    }
    if (eventName !== 'change') {
      fire('change', payload);
    }
  }

  // ── Core operations ────────────────────────────────────────
  function has(handle) {
    return items.indexOf(handle) !== -1;
  }

  function add(handle) {
    if (!handle || has(handle)) return false;
    items.unshift(handle); // newest first
    write(items);
    syncDOM();
    fire('add', { handle: handle, list: items.slice() });
    return true;
  }

  function remove(handle) {
    var idx = items.indexOf(handle);
    if (idx === -1) return false;
    items.splice(idx, 1);
    write(items);
    syncDOM();
    fire('remove', { handle: handle, list: items.slice() });
    return true;
  }

  function toggle(handle) {
    if (has(handle)) {
      remove(handle);
      return false;
    } else {
      add(handle);
      return true;
    }
  }

  function clear() {
    items = [];
    write(items);
    syncDOM();
    fire('change', { handle: null, list: [] });
  }

  // ── DOM sync ───────────────────────────────────────────────
  function syncHeader() {
    var headerLinks = document.querySelectorAll('[data-fav-header]');
    var count = items.length;
    headerLinks.forEach(function (link) {
      var bubble = link.querySelector('[data-fav-count]');
      if (bubble) {
        bubble.textContent = count > 99 ? '99+' : String(count);
      }
      if (count > 0) {
        link.classList.add('has-favs');
      } else {
        link.classList.remove('has-favs');
      }
    });
  }

  function syncButtons() {
    var buttons = document.querySelectorAll('[data-fav-toggle]');
    buttons.forEach(function (btn) {
      var handle = btn.getAttribute('data-fav-handle');
      if (!handle) return;
      if (has(handle)) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Remove from favourites');
        btn.setAttribute('title', 'Remove from favourites');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Save to favourites');
        btn.setAttribute('title', 'Save to favourites');
      }
    });
  }

  function syncDOM() {
    syncHeader();
    syncButtons();
  }

  // ── Click handler (delegated, works for any future card) ───
  function onClick(e) {
    var btn = e.target.closest && e.target.closest('[data-fav-toggle]');
    if (!btn) return;

    // Stop the click bubbling to parent <a> on the card
    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-fav-handle');
    if (!handle) return;

    var nowActive = toggle(handle);

    // Animate the clicked button
    btn.classList.remove('is-popping');
    // Force reflow so the animation can replay
    void btn.offsetWidth;
    btn.classList.add('is-popping');

    // Animate the header bubble if it grew
    if (nowActive) {
      var bubble = document.querySelector('[data-fav-header] [data-fav-count]');
      if (bubble) {
        bubble.classList.remove('bumping');
        void bubble.offsetWidth;
        bubble.classList.add('bumping');
      }
    }
  }

  // ── Cross-tab sync ─────────────────────────────────────────
  function onStorage(e) {
    if (e.key !== STORAGE_KEY) return;
    items = read();
    syncDOM();
    fire('change', { handle: null, list: items.slice() });
  }

  // ── MutationObserver — wire new cards on the fly ───────────
  // Cheap: we only re-sync, no per-button setup needed (clicks
  // are delegated to document)
  var domObserver = null;
  function observeDOM() {
    if (!('MutationObserver' in window)) return;
    if (domObserver) return;
    domObserver = new MutationObserver(function (mutations) {
      // Only re-sync if any added node contains a fav toggle
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-fav-toggle]')) {
            syncButtons();
            return;
          }
          if (node.querySelector && node.querySelector('[data-fav-toggle]')) {
            syncButtons();
            return;
          }
        }
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  // ── Public API ─────────────────────────────────────────────
  window.TimbaFavourites = {
    list: function () { return items.slice(); },
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear,
    count: function () { return items.length; },
    on: function (eventName, cb) {
      if (!listeners[eventName]) return;
      listeners[eventName].push(cb);
    },
    off: function (eventName, cb) {
      var arr = listeners[eventName];
      if (!arr) return;
      var idx = arr.indexOf(cb);
      if (idx !== -1) arr.splice(idx, 1);
    },
    // For the favourites page to force a re-sync after DOM swap
    refresh: syncDOM
  };

  // ── Boot ───────────────────────────────────────────────────
  function boot() {
    items = read();
    syncDOM();
    document.addEventListener('click', onClick);
    window.addEventListener('storage', onStorage);
    observeDOM();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();